import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { compact } from "lodash-es";
import React, { ReactNode, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";

import {
    ANNUAL_INCOME_DISPLAY_NAMES,
    AnnualIncome,
    Occupation,
    OCCUPATION_DISPLAY_NAMES,
    SOURCE_OF_INCOME_DISPLAY_NAMES,
    SourceOfIncome,
} from "../../types/unit";
import { preventFormSubmitOnEnter } from "../../utils/formUtils";
import { ControllerStringOptionSelect } from "../form/ControllerStringOptionSelect";
import { FormInputWrapper } from "../form/FormInputWrapper";
import { IStringOption } from "../input/StringOptionSelect";
import {
    BeneficialOwner,
    IAdditionalInformationFormData,
    IBeneficialOwnersFormData,
    IOfficerInformationFormData,
} from "./FormData";

const formSchema = yup.object({
    officer: yup.object({
        occupation: yup
            .mixed<Occupation>()
            .oneOf(Object.keys(OCCUPATION_DISPLAY_NAMES) as Occupation[])
            .required()
            .label("Occupation"),
        annualIncome: yup
            .mixed<AnnualIncome>()
            .oneOf(Object.keys(ANNUAL_INCOME_DISPLAY_NAMES) as AnnualIncome[])
            .required()
            .label("Annual Income"),
        sourceOfIncome: yup
            .mixed<SourceOfIncome>()
            .oneOf(Object.keys(SOURCE_OF_INCOME_DISPLAY_NAMES) as SourceOfIncome[])
            .required()
            .label("Source of Income"),
    }),
    beneficialOwners: yup
        .array()
        .of(
            yup.object({
                occupation: yup
                    .mixed<Occupation>()
                    .oneOf(Object.keys(OCCUPATION_DISPLAY_NAMES) as Occupation[])
                    .required()
                    .label("Occupation"),
                annualIncome: yup
                    .mixed<AnnualIncome>()
                    .oneOf(Object.keys(ANNUAL_INCOME_DISPLAY_NAMES) as AnnualIncome[])
                    .required()
                    .label("Annual Income"),
                sourceOfIncome: yup
                    .mixed<SourceOfIncome>()
                    .oneOf(Object.keys(SOURCE_OF_INCOME_DISPLAY_NAMES) as SourceOfIncome[])
                    .required()
                    .label("Source of Income"),
            }),
        )
        .optional(),
});

interface IProps {
    officerData: IOfficerInformationFormData;
    beneficialOwnersData: IBeneficialOwnersFormData;
    onSubmit: SubmitHandler<IAdditionalInformationFormData>;

    defaultValues?: IAdditionalInformationFormData;
    prevPageButton?: ReactNode;
    nextPageButton?: ReactNode;
}

export const AdditionalInformationForm: React.FC<IProps> = ({
    officerData,
    beneficialOwnersData,
    defaultValues,
    onSubmit,
    prevPageButton,
    nextPageButton,
}) => {
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const resolver = yupResolver(formSchema);
    const methods = useForm<IAdditionalInformationFormData>({
        mode: "onTouched",
        resolver,
        defaultValues,
    });
    const { handleSubmit } = methods;

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventFormSubmitOnEnter}>
                <Stack spacing={3}>
                    <Typography>
                        Please provide a few additional details for the officer and beneficial owners.
                    </Typography>
                    <OfficerAdditionalFields officerData={officerData} />
                    {beneficialOwnersData.beneficialOwner.map(
                        (owner, i) =>
                            !owner.isOfficer && <BeneficialOwnerAdditionalFields key={i} index={i} ownerData={owner} />,
                    )}
                    <Stack direction="row" justifyContent="space-between">
                        <div>{prevPageButton}</div>
                        <div>{nextPageButton}</div>
                    </Stack>
                </Stack>
            </form>
        </FormProvider>
    );
};

interface IOfficerAdditionalFieldsProps {
    officerData: IOfficerInformationFormData;
}

const OfficerAdditionalFields: React.FC<IOfficerAdditionalFieldsProps> = ({ officerData }) => {
    const officerDisplayName = compact([officerData.firstName, officerData.lastName]).join(" ");
    return (
        <Stack spacing={1}>
            <Typography fontWeight="semiBold">Officer: {officerDisplayName}</Typography>
            <FormInputWrapper<IAdditionalInformationFormData>
                label={`${officerDisplayName}'s Occupation`}
                name="officer.occupation"
                required={true}
            >
                <ControllerStringOptionSelect<Occupation, IAdditionalInformationFormData>
                    name="officer.occupation"
                    options={OCCUPATION_OPTIONS}
                    placeholder="Select occupation"
                    required={true}
                />
            </FormInputWrapper>
            <FormInputWrapper<IAdditionalInformationFormData>
                label={`${officerDisplayName}'s Annual Income`}
                name="officer.annualIncome"
                required={true}
            >
                <ControllerStringOptionSelect<AnnualIncome, IAdditionalInformationFormData>
                    name="officer.annualIncome"
                    options={ANNUAL_INCOME_OPTIONS}
                    placeholder="Select annual income"
                    required={true}
                />
            </FormInputWrapper>
            <FormInputWrapper<IAdditionalInformationFormData>
                label={`${officerDisplayName}'s Source of Income`}
                name="officer.sourceOfIncome"
                required={true}
            >
                <ControllerStringOptionSelect<SourceOfIncome, IAdditionalInformationFormData>
                    name="officer.sourceOfIncome"
                    options={SOURCE_OF_INCOME_OPTIONS}
                    placeholder="Select source of income"
                    required={true}
                />
            </FormInputWrapper>
        </Stack>
    );
};

interface IBeneficialOwnerAdditionalFieldsProps {
    index: number;
    ownerData: BeneficialOwner;
}

const BeneficialOwnerAdditionalFields: React.FC<IBeneficialOwnerAdditionalFieldsProps> = ({ index: i, ownerData }) => {
    const ownerDisplayName = compact([ownerData.firstName, ownerData.lastName]).join(" ");
    return (
        <Stack spacing={1}>
            <Typography fontWeight="semiBold">Beneficial Owner: {ownerDisplayName}</Typography>
            <FormInputWrapper<IAdditionalInformationFormData>
                label={`${ownerDisplayName}'s Occupation`}
                name={`beneficialOwners.${i}.occupation`}
                required={true}
            >
                <ControllerStringOptionSelect<Occupation, IAdditionalInformationFormData>
                    name={`beneficialOwners.${i}.occupation`}
                    options={OCCUPATION_OPTIONS}
                    placeholder="Select occupation"
                    required={true}
                />
            </FormInputWrapper>
            <FormInputWrapper<IAdditionalInformationFormData>
                label={`${ownerDisplayName}'s Annual Income`}
                name={`beneficialOwners.${i}.annualIncome`}
                required={true}
            >
                <ControllerStringOptionSelect<AnnualIncome, IAdditionalInformationFormData>
                    name={`beneficialOwners.${i}.annualIncome`}
                    options={ANNUAL_INCOME_OPTIONS}
                    placeholder="Select annual income"
                    required={true}
                />
            </FormInputWrapper>
            <FormInputWrapper<IAdditionalInformationFormData>
                label={`${ownerDisplayName}'s Source of Income`}
                name={`beneficialOwners.${i}.sourceOfIncome`}
                required={true}
            >
                <ControllerStringOptionSelect<SourceOfIncome, IAdditionalInformationFormData>
                    name={`beneficialOwners.${i}.sourceOfIncome`}
                    options={SOURCE_OF_INCOME_OPTIONS}
                    placeholder="Select source of income"
                    required={true}
                />
            </FormInputWrapper>
        </Stack>
    );
};

const OCCUPATION_OPTIONS: Array<IStringOption<Occupation>> = Object.entries(OCCUPATION_DISPLAY_NAMES).map(
    ([key, displayName]) => ({ value: key as Occupation, label: displayName }),
);

const ANNUAL_INCOME_OPTIONS: Array<IStringOption<AnnualIncome>> = Object.entries(ANNUAL_INCOME_DISPLAY_NAMES).map(
    ([key, displayName]) => ({ value: key as AnnualIncome, label: displayName }),
);

const SOURCE_OF_INCOME_OPTIONS: Array<IStringOption<SourceOfIncome>> = Object.entries(
    SOURCE_OF_INCOME_DISPLAY_NAMES,
).map(([key, displayName]) => ({ value: key as SourceOfIncome, label: displayName }));
